import type { CollectionBehaviorSource, CollectionConfidence } from './ar-collection.js';

// Billing AR forecast contract (ALC-1422 / ALC-1425 / ALC-1426)
//
// The stable Billing → Cash Flow contract for expected AR inflows: one row
// per open payment schedule, so downstream consumers can dedup
// deterministically on `scheduleId`. Forecast-only — rows never create
// accounting entries, and there is no ECL/allowance semantics here.

/**
 * Confidence of the projected usage portion on a projected cycle row (ALC-1426).
 * - `HIGH` — three or more FINALIZED observations in the lookback sample.
 * - `LOW` — one or two observations (projection still emitted).
 * - `UNAVAILABLE` — no FINALIZED history; usage is omitted (never assumed zero).
 * Null when the cycle has no variable items (fixed-only / existing AR).
 */
export const UsageProjectionConfidence = {
  HIGH: 'HIGH',
  LOW: 'LOW',
  UNAVAILABLE: 'UNAVAILABLE',
} as const;
export type UsageProjectionConfidence =
  (typeof UsageProjectionConfidence)[keyof typeof UsageProjectionConfidence];

/**
 * `PAST_DUE` when the schedule's contractual due date is before the forecast
 * reference date. Past-due receivables are never blended silently into
 * future inflows — consumers must be able to segregate the bucket.
 */
export type BillingArForecastBucket = 'FUTURE' | 'PAST_DUE';

/**
 * Where a forecast row comes from (ALC-1425 / ALC-1427).
 * - `EXISTING_AR` — an issued invoice's open payment schedule (COMMITTED for Cash Flow).
 * - `PROJECTED_FIXED_CYCLE` — a pre-invoice fixed billing-cycle projection through the
 *   current term end (FORECAST_AI). Superseded when the matching cycle's invoice becomes
 *   ISSUED/PARTIALLY_PAID.
 * - `RENEWAL_ASSUMED` — auto-renew projection past the current term end at renewed terms
 *   (FORECAST_AI). Never mistaken for contracted value; superseded without duplication
 *   when a real renewal advances the term (ALC-1427).
 */
export const BillingArForecastRowSource = {
  EXISTING_AR: 'EXISTING_AR',
  PROJECTED_FIXED_CYCLE: 'PROJECTED_FIXED_CYCLE',
  RENEWAL_ASSUMED: 'RENEWAL_ASSUMED',
} as const;
export type BillingArForecastRowSource =
  (typeof BillingArForecastRowSource)[keyof typeof BillingArForecastRowSource];

/** Pre-invoice sources Cash Flow tags as FORECAST_AI (not contracted COMMITTED). */
export function isBillingArForecastAiSource(source: BillingArForecastRowSource): boolean {
  return (
    source === BillingArForecastRowSource.PROJECTED_FIXED_CYCLE ||
    source === BillingArForecastRowSource.RENEWAL_ASSUMED
  );
}

/** Backend-sortable columns of the forecast listing. */
export type BillingArForecastSortField =
  'dueDate' | 'expectedCashDate' | 'grossAmount' | 'riskAdjustedAmount' | 'collectionProbability';

export type BillingArForecastSortOrder = 'asc' | 'desc';

export const BillingArForecastProbabilityRange = {
  ZERO_TO_TWENTY_FIVE: '0_25',
  TWENTY_FIVE_TO_FIFTY: '25_50',
  FIFTY_TO_SEVENTY_FIVE: '50_75',
  SEVENTY_FIVE_TO_ONE_HUNDRED: '75_100',
} as const;
export type BillingArForecastProbabilityRange =
  (typeof BillingArForecastProbabilityRange)[keyof typeof BillingArForecastProbabilityRange];

/** Query parameters of the forecast endpoint, shared by server and clients. */
export interface BillingArForecastQueryParams {
  /** Restrict to one legal entity. */
  entityId?: string;
  /** Restrict to one customer. */
  customerId?: string;
  /**
   * Inclusive cash-relevance cap (ISO date): a row stays when its contractual
   * due date OR its expected cash date is on/before the cap — early payers
   * surface without a due date inside the horizon. Past-due rows are always
   * inside any horizon.
   */
  horizonEnd?: string;
  /**
   * Anchor date (ISO date) for re-aging, bucketing, and daysPastDue —
   * defaults to today. Callers reproducing a projection they already hold
   * (e.g. Cash Flow drill-down, ALC-1424 CFO-01) MUST pass their original
   * anchor, or a day rollover re-ages rows into different weeks and the
   * drill-down stops reconciling with the reported cell.
   */
  referenceDate?: string;
  /**
   * Restrict rows to one bucket (drives the Em atraso / Futuro tabs on the
   * listing page). Omitted → both buckets. Applied AFTER `totals` and
   * `bucketCounts` are computed, so switching tabs never changes the
   * summary cards or the other tab's count.
   */
  bucket?: BillingArForecastBucket;
  /** Restrict rows to one row source. Omitted → all sources. */
  source?: BillingArForecastRowSource;
  /** Restrict rows to one or more collection-probability ranges. Omitted → all ranges. */
  collectionProbabilityRanges?: BillingArForecastProbabilityRange[];
  /** Backend sort column. Defaults to `expectedCashDate` when omitted. */
  sortBy?: BillingArForecastSortField;
  /** Defaults to `asc`. */
  sortOrder?: BillingArForecastSortOrder;
  /**
   * 1-based page number. Pagination is an HTTP-listing concern only: when
   * omitted, the full filtered row set is returned unsliced — in-process
   * callers (Cash Flow's `BillingArForecastService.getCashFlowForecast()`) never
   * pass this and must keep receiving every row.
   */
  page?: number;
  /**
   * Page size. Activates pagination on its own (`page` defaults to `1` when
   * omitted alongside it). Leave both unset to receive the full row set.
   */
  limit?: number;
}

/** Collection-behavior metadata carried by a forecast row. */
export interface BillingArForecastBehavior {
  /** Median (receipt date − due date) in days for the customer's scope. */
  medianLagDays: number;
  sampleSize: number;
  source: CollectionBehaviorSource;
  confidence: CollectionConfidence;
}

/** Collection-rate metadata behind the row's risk adjustment. */
export interface BillingArForecastRisk {
  sampleSize: number;
  source: CollectionBehaviorSource;
  confidence: CollectionConfidence;
}

export interface BillingArForecastRow {
  /**
   * Deterministic dedup key. Real schedules use `ArPaymentSchedule.scheduleId`;
   * projected fixed-cycle installments use
   * `proj:{contractId}:{periodStart}:{installmentNumber}` (ALC-1425);
   * renewal-assumed installments use
   * `renew:{contractId}:{periodStart}:{installmentNumber}` (ALC-1427).
   */
  scheduleId: string;
  /**
   * Invoice id for existing AR, or a synthetic `proj:` / `renew:` key for
   * pre-invoice projections (never a real invoice row).
   */
  invoiceId: string;
  installmentNumber: number;
  entityId: string;
  customerId: string;
  /** Display metadata enriched by Billing for the Cash Flow consumer. */
  customerName?: string | null;
  contractCodes?: string[];
  /**
   * Billing contracts behind the invoice — empty for manual/non-contract AR,
   * and possibly several: consolidated invoices serve multiple contracts'
   * billing cycles (ALC-1199). A projected fixed-cycle row carries exactly one.
   */
  contractIds: string[];
  /**
   * The contract items (`BillingContractItem.itemId`) that priced a pre-invoice row: the
   * advance items of the cycle's version and the arrears items of the previous cycle's
   * version. A cycle after an amendment names the new version's items. Empty for existing AR.
   */
  contractItemIds: string[];
  /**
   * The invoice's explicit currency. Null when the invoice carries none —
   * per the currency policy, a nullable currency is NEVER defaulted to BRL;
   * consumers resolve the owning entity's functional currency instead.
   */
  currencyCode: string | null;
  /**
   * Current AR invoice status for existing AR, or `PROJECTED` for pre-invoice
   * fixed-cycle / renewal-assumed rows (ALC-1425 / ALC-1427).
   */
  invoiceStatus: string;
  /** Contractual due date (ISO date, YYYY-MM-DD). */
  dueDate: string;
  /**
   * Expected cash date: due date shifted by the learned collection lag and
   * re-aged so it is never before the reference date — equivalently
   * `referenceDate + max(0, medianLagDays − daysPastDue)`.
   */
  expectedCashDate: string;
  bucket: BillingArForecastBucket;
  /** Whole days the contractual due date is past the reference date; 0 when FUTURE. */
  daysPastDue: number;
  /** Unpaid remainder of the schedule (amount − paid), in the row's currency. */
  grossAmount: number;
  /** `grossAmount × collectionProbability`, same currency. */
  riskAdjustedAmount: number;
  /** Collection rate applied to this row, in [0, 1]; 1 when no history exists. */
  collectionProbability: number;
  behavior: BillingArForecastBehavior;
  risk: BillingArForecastRisk;
  /**
   * Existing issued AR, in-term fixed-cycle projection, or renewal-assumed
   * projection past term end (ALC-1425 / ALC-1427).
   */
  source: BillingArForecastRowSource;
  /**
   * Fixed (committed) portion of `grossAmount` on a projected cycle (ALC-1426).
   * Null for existing AR. On projected rows with priced usage,
   * `fixedGrossAmount + usageGrossAmount` equals `grossAmount`. When usage is
   * UNAVAILABLE, `usageGrossAmount` is null and `grossAmount === fixedGrossAmount`.
   */
  fixedGrossAmount: number | null;
  /**
   * Projected usage portion of `grossAmount` (ALC-1426). Null for existing AR and
   * when confidence is UNAVAILABLE (never invents a zero-quantity charge — the
   * usage line is simply omitted from pricing).
   */
  usageGrossAmount: number | null;
  /**
   * Usage-projection confidence for this cycle (ALC-1426). Null when there are
   * no variable items on the projected version (or for existing AR).
   */
  usageConfidence: UsageProjectionConfidence | null;
  /** FINALIZED observations that produced the usage projection; null when N/A. */
  usageObservationCount: number | null;
}

/**
 * Totals are per currency — amounts in different currencies never sum. The
 * null-currency group aggregates invoices with no explicit currency.
 */
export interface BillingArForecastCurrencyTotals {
  currencyCode: string | null;
  rowCount: number;
  grossTotal: number;
  riskAdjustedTotal: number;
  futureGross: number;
  futureRiskAdjusted: number;
  pastDueGross: number;
  pastDueRiskAdjusted: number;
}

/** Row counts per bucket, computed after entity/customer/source filters but BEFORE the
 *  `bucket` filter and before pagination — so a listing page can render both tab counts
 *  regardless of which tab (or page) is currently selected. */
export interface BillingArForecastBucketCounts {
  pastDue: number;
  future: number;
}

/** Pagination echo. `limit` mirrors the request when `page` was supplied; otherwise it
 *  equals `totalCount` and every row of the filtered set is returned unsliced. */
export interface BillingArForecastPagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export interface BillingArForecastResult {
  /** ISO date (YYYY-MM-DD) the forecast was computed against. */
  referenceDate: string;
  /** Tenant lookback setting (invoices per scope) the estimates used. */
  lookbackInvoices: number;
  /**
   * Tenant lookback (FINALIZED usage periods) used for variable-item quantity
   * projection (ALC-1426). Defaults to 3.
   */
  usageForecastLookbackPeriods: number;
  /**
   * Rows sorted by `sortBy`/`sortOrder` (default `expectedCashDate` asc), always
   * tie-broken by `scheduleId`. Unsliced unless the request supplied `page`.
   */
  rows: BillingArForecastRow[];
  /** Computed over the full entity/customer/source/horizon-filtered set — never just
   *  the current bucket or page. */
  totals: BillingArForecastCurrencyTotals[];
  bucketCounts: BillingArForecastBucketCounts;
  pagination: BillingArForecastPagination;
}
